import React, { FC, useState, useEffect, useRef } from "react";
import { Spin, Table, TableProps, Slider } from "antd";
import type { FilterDropdownProps, FilterValue } from "antd/lib/table/interface";
import {
  ExternalMatter,
  AccruedFee,
  BaseAccruedFee,
  formatPrice,
  RelevantMatter,
  RelevantMatterWithAccruedFees,
} from "../../data-types";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { FilterFilled } from "@ant-design/icons";
import {
  createClientFilterWithPreselection,
  filterOrgUnit,
  filterTitle,
  filterUpdatedAt,
} from "~/containers/matter-table/filter";
import { useOrganisationalUnitsQuery } from "~/graphql/query";
import { MatterFilter } from "../../data-types";
import { MatterTitleScreenTabKey } from "../create-proposal/relevant-matter-tabs";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
const colours = {
  vpd: "#148c9f",
  vpdHover: "#19b4cc",
  nonVPD: "#b8372e",
  nonVPDHover: "#d15147",
};

const wrapText = (text: string, maxWidth: number): string[] => {
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word) => {
    if ((currentLine + word).length <= maxWidth) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

type ExternalMatterWithRelevanceScore = ExternalMatter & {
  relevanceScore?: number;
};

interface HistoricComparisonProps {
  historicalMatters: RelevantMatter[];
  externalMatters: ExternalMatterWithRelevanceScore[];
  accruedFees: AccruedFee[];
  externalAccruedFees: BaseAccruedFee[];
  loading: boolean;
  selectedMatter: RelevantMatter | null;
  setSelectedMatter: (matter: RelevantMatter | null) => void;
  selectedExternalMatter: ExternalMatter | null;
  setSelectedExternalMatter: (matter: ExternalMatter | null) => void;
  renderRelevantMatterPage: () => React.ReactNode;
  matterFilter: MatterFilter;
  handleFilterChange: (filters: Record<string, FilterValue | null>) => void;
  activeTab: MatterTitleScreenTabKey;
  currencySymbol: string;
}

export const HistoricComparison: FC<HistoricComparisonProps> = ({
  historicalMatters,
  externalMatters,
  accruedFees,
  externalAccruedFees,
  loading,
  selectedMatter,
  setSelectedMatter,
  selectedExternalMatter,
  setSelectedExternalMatter,
  renderRelevantMatterPage,
  matterFilter,
  handleFilterChange,
  activeTab,
  currencySymbol,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(0);
  const [relevanceFilter, setRelevanceFilter] = useState(0);

  const taxonomy = useTaxonomyLabel();

  const mattersWithAccruedFees: RelevantMatterWithAccruedFees[] =
    historicalMatters?.map((matter) => ({
      ...matter,
      accruedFees: accruedFees?.find((fee) => fee.matterId === matter.id),
    })) || [];
  const externalMattersWithAccruedFees =
    externalMatters?.map((matter) => ({
      ...matter,
      accruedFees: externalAccruedFees?.find((fee) => fee.externalMatterId === matter.id),
    })) || [];
  const matterChartData = mattersWithAccruedFees.map((matter) => ({
    x: matter.accruedFees?.actualFee,
    y: matter.accruedFees?.actualGpm,
    relevance: 100 * (matter.relevanceScore || 0),
    id: matter.id,
    title: matter.title,
    type: "internal",
  }));
  const externalMatterChartData = externalMattersWithAccruedFees.map((matter) => ({
    x: matter.accruedFees?.actualFee,
    y: matter.accruedFees?.actualGpm,
    relevance: 100 * (matter.relevanceScore || 0),
    id: matter.id,
    title: matter.title,
    type: "external",
  }));

  const handleClickMatter = (datum: any) => {
    setSelectedMatter(historicalMatters.find((matter) => matter.id === datum.id) ?? null);
  };

  const handleClickExternalMatter = (datum: any) => {
    setSelectedExternalMatter(externalMatters.find((matter) => matter.id === datum.id) ?? null);
  };

  const isLogScale = (() => {
    const allFees = [...matterChartData, ...externalMatterChartData]
      .map((data) => data.x)
      .filter((fee): fee is number => fee !== undefined && fee > 0);

    if (allFees.length < 2) return false;

    const maxFee = Math.max(...allFees);
    const minFee = Math.min(...allFees);
    const shouldUseLog = maxFee / minFee > 100;

    return shouldUseLog;
  })();

  // Filter out zero values when using log scale
  const filterDataForScale = (data: typeof matterChartData) => {
    if (isLogScale) {
      const filtered = data.filter(
        (point) =>
          typeof point.x === "number" && typeof point.y === "number" && point.x > 0 && point.y > 0
      );
      return filtered;
    }
    return data;
  };

  // Filter the chart data based on relevance and scale requirements
  const filteredMatterChartData = filterDataForScale(
    matterChartData.filter((matter) => matter.relevance >= relevanceFilter)
  );
  const filteredExternalMatterChartData = filterDataForScale(
    externalMatterChartData.filter((matter) => matter.relevance >= relevanceFilter)
  );

  // Generate tick values for axes
  const calculateYAxisDomain = (): [number | "auto", number | "auto"] => {
    const allGpmValues = [...filteredMatterChartData, ...filteredExternalMatterChartData]
      .map((data) => data.y)
      .filter((gpm): gpm is number => gpm !== undefined);

    if (allGpmValues.length === 0) return ["auto", "auto"];

    const maxGpm = Math.max(...allGpmValues);
    const minGpm = Math.min(...allGpmValues);

    if (maxGpm > 100) {
      return ["auto", "auto"];
    }

    let yMin: number;
    if (minGpm < 0) {
      yMin = Math.floor(minGpm * 1.1);
    } else if (minGpm < 10) {
      yMin = 0;
    } else {
      const paddingAmount = Math.max(5, (maxGpm - minGpm) * 0.1);
      yMin = Math.max(0, Math.floor(minGpm - paddingAmount));
    }

    const paddingFactor = 0.2;
    const paddedMax = maxGpm + (maxGpm - minGpm) * paddingFactor;

    let roundingFactor: number;
    if (maxGpm <= 20) roundingFactor = 5;
    else if (maxGpm <= 50) roundingFactor = 10;
    else if (maxGpm <= 100) roundingFactor = 20;
    else roundingFactor = 25;

    let yMax = Math.ceil(paddedMax / roundingFactor) * roundingFactor;
    yMax = Math.min(yMax, 100);

    return [yMin, yMax];
  };

  const getYAxisTicks = (): number[] => {
    const domain = calculateYAxisDomain();
    if (domain[0] === "auto" || domain[1] === "auto") return [];

    const minY = domain[0] as number;
    const maxY = domain[1] as number;
    const range = maxY - minY;

    let tickInterval: number;
    if (range <= 20) tickInterval = 5;
    else if (range <= 50) tickInterval = 10;
    else if (range <= 100) tickInterval = 20;
    else tickInterval = 25;

    const startTick = Math.ceil(minY / tickInterval) * tickInterval;
    const ticks: number[] = [];

    for (let tick = startTick; tick <= maxY; tick += tickInterval) {
      ticks.push(tick);
    }

    if (minY < startTick - tickInterval / 2) {
      ticks.unshift(minY);
    }

    if (minY < 0 && maxY > 0 && !ticks.includes(0)) {
      ticks.push(0);
      ticks.sort((a, b) => a - b);
    }

    if (!ticks.includes(maxY)) {
      ticks.push(maxY);
    }

    return ticks;
  };

  const getLogScaleTicks = () => {
    const allFees = [...filteredMatterChartData, ...filteredExternalMatterChartData]
      .map((data) => data.x)
      .filter((fee): fee is number => fee !== undefined && fee > 0);

    if (allFees.length === 0) return [];

    const minFee = Math.min(...allFees);
    const maxFee = Math.max(...allFees);

    // Find the power of 10 range that covers our data
    const minPow = Math.floor(Math.log10(minFee));
    const maxPow = Math.ceil(Math.log10(maxFee));

    const ticks: number[] = [];
    for (let i = minPow; i <= maxPow; i++) {
      // Add the power of 10
      ticks.push(Math.pow(10, i));
      // Add the halfway point if it's within our range
      const halfwayPoint = 5 * Math.pow(10, i - 1);
      if (halfwayPoint >= minFee && halfwayPoint <= maxFee) {
        ticks.push(halfwayPoint);
      }
    }

    return ticks.sort((a, b) => a - b);
  };

  const getLinearScaleTicks = () => {
    const allFees = [...filteredMatterChartData, ...filteredExternalMatterChartData]
      .map((data) => data.x)
      .filter((fee): fee is number => fee !== undefined && fee > 0);

    if (allFees.length === 0) return [];

    const minFee = Math.min(...allFees);
    const maxFee = Math.max(...allFees);
    const tickCount = 6;
    const range = maxFee - minFee;
    const rawStep = range / tickCount;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));

    let niceStep = magnitude;
    if (rawStep / magnitude > 5) {
      niceStep = 10 * magnitude;
    } else if (rawStep / magnitude > 2) {
      niceStep = 5 * magnitude;
    } else if (rawStep / magnitude > 1) {
      niceStep = 2 * magnitude;
    }

    const startTick = Math.floor(minFee / niceStep) * niceStep;
    const ticks: number[] = [];
    for (let tick = startTick; tick <= maxFee; tick += niceStep) {
      ticks.push(tick);
    }
    return ticks;
  };

  // Check no data condition using filtered data
  const notEnoughData =
    filteredExternalMatterChartData.length + filteredMatterChartData.length <= 1;

  const { data: orgUnits } = useOrganisationalUnitsQuery();

  const handleTableChange: TableProps<RelevantMatterWithAccruedFees>["onChange"] = (
    pagination,
    filters
  ) => {
    handleFilterChange(filters);
  };

  useEffect(() => {
    if (chartContainerRef.current && activeTab === MatterTitleScreenTabKey.HistoricalComparison) {
      const rect = chartContainerRef.current.getBoundingClientRect();
      const topOffset = rect.top;
      const viewportHeight = window.innerHeight;
      const height = viewportHeight - topOffset;
      setChartHeight(height - 80);
    }
  }, [matterChartData, externalMatterChartData, activeTab]);

  const filterTableColumns = [
    {
      title: "Filters:",
      dataIndex: "title",
      key: "title",
      width: "10%",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filterDropdown: (values: FilterDropdownProps) =>
        filterTitle({
          ...values,
          defaultValue: matterFilter.title || undefined,
          setSelectedKeys: (key: string) => values.setSelectedKeys([key]),
        }),
      filteredValue: matterFilter.title ? [matterFilter.title] : undefined,
    },
    {
      title: "Client",
      dataIndex: ["client", "name"],
      key: "client",
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filterDropdown: createClientFilterWithPreselection(matterFilter.client || []),
      filteredValue: matterFilter.client || null,
    },
    {
      title: taxonomy.PracticeArea,
      dataIndex: ["organisationalUnit", "name"],
      key: "orgUnit",
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filterDropdown: filterOrgUnit(orgUnits?.organisationalUnits || []),
      filteredValue: matterFilter.orgUnit ? [matterFilter.orgUnit] : null,
    },
    {
      title: "Updated At",
      dataIndex: ["updatedAt"],
      key: "updatedAt",
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filterDropdown: filterUpdatedAt,
      filteredValue: matterFilter.updatedAt
        ? [matterFilter.updatedAt.from?.getTime() || 0, matterFilter.updatedAt.to?.getTime() || 0]
        : null,
      sorter: (a: RelevantMatterWithAccruedFees, b: RelevantMatterWithAccruedFees) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div>Relevance:</div>
          <Slider
            defaultValue={0}
            max={maxRelevance}
            style={{
              flex: 1,
              marginRight: 16,
            }}
            onChange={(value) => {
              setRelevanceFilter(value);
            }}
          />
        </div>
      ),
      width: "40%",
      dataIndex: ["relevance"],
      key: "relevance",
    },
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const wrappedTitle = wrapText(data.title, 30);

      const tooltipStyle = {
        backgroundColor: "white",
        padding: "10px",
        border: "1px solid #ccc",
      };

      const tooltipLineStyle = {
        lineHeight: "1.2",
        margin: "0",
      };

      return (
        <div style={tooltipStyle}>
          <div>
            {wrappedTitle.map((line, index) => (
              <div key={index} style={tooltipLineStyle}>
                {line}
              </div>
            ))}
          </div>
          <div style={tooltipLineStyle}>Fee: {formatPrice(data.x, currencySymbol)}</div>
          <div style={tooltipLineStyle}>GPM: {data.y.toFixed(1)}%</div>
        </div>
      );
    }
    return null;
  };

  // Calculate max relevance from all data points
  const maxRelevance = Math.floor(
    Math.max(
      ...matterChartData.map((m) => m.relevance),
      ...externalMatterChartData.map((m) => m.relevance)
    )
  );

  return selectedMatter || selectedExternalMatter ? (
    <>{renderRelevantMatterPage()}</>
  ) : (
    <>
      <Table
        columns={filterTableColumns}
        dataSource={[]}
        pagination={false}
        onChange={handleTableChange}
        showHeader={true}
        style={{ marginBottom: 0 }}
        locale={{ emptyText: " " }}
        scroll={{ y: 0 }}
      />
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
          <Spin />
        </div>
      ) : notEnoughData ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
          <p>No data available to display.</p>
        </div>
      ) : (
        <div
          ref={chartContainerRef}
          style={{ height: chartHeight, maxWidth: "1000px", margin: "0 auto" }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{
                top: externalMatterChartData.length > 0 ? 40 : 20,
                right: 20,
                bottom: 60,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <Scatter
                name="Domain Helper"
                data={filteredMatterChartData}
                hide={true}
                legendType="none"
              />
              <Scatter
                name="Domain Helper"
                data={filteredExternalMatterChartData}
                hide={true}
                legendType="none"
              />
              <XAxis
                type="number"
                dataKey="x"
                name="Fee"
                scale={isLogScale ? "log" : "linear"}
                tickFormatter={(value) => `${currencySymbol}${value.toLocaleString()}`}
                ticks={isLogScale ? getLogScaleTicks() : getLinearScaleTicks()}
                label={{
                  value: `Fee Charged ${isLogScale ? "(Logarithmic Scale)" : ""}`,
                  position: "bottom",
                  offset: 10,
                  style: { fontWeight: "bold" },
                }}
                domain={["auto", "auto"]}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="GPM"
                unit="%"
                label={{
                  value: "GPM",
                  angle: -90,
                  position: "insideLeft",
                  style: { fontWeight: "bold" },
                }}
                domain={calculateYAxisDomain()}
                allowDataOverflow={true}
                ticks={getYAxisTicks()}
              />
              <ZAxis type="category" dataKey="type" name="Type" range={[130, 130]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                align="center"
                layout="horizontal"
                wrapperStyle={{ position: "relative", marginTop: "10px", paddingLeft: "60px" }}
              />
              <Scatter
                name="VPD Matters"
                data={filteredMatterChartData}
                fill={colours.vpd}
                onClick={(data) => handleClickMatter(data)}
              />
              <Scatter
                name="Non-VPD Matters"
                data={filteredExternalMatterChartData}
                fill={colours.nonVPD}
                onClick={(data) => handleClickExternalMatter(data)}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
};
